import React, { FC, useRef, useState } from "react";
import { Card, Empty } from "antd";
import { EyeOutlined, WarningOutlined } from "@ant-design/icons";
import styles from "./pms-matters-overview.module.css";
import { graphql } from "~/gql";
import { useQuery } from "@apollo/client";
import { formatPrice } from "~/design-system/data-types";
import { useSettings } from "~/hooks";

const {
  ["overview-card"]: overviewCardClassName,
  ["stats-section"]: statsSectionClassName,
  ["action-section"]: actionSectionClassName,
  ["empty-state"]: emptyStateClassName,
  shake: shakeClassName,
} = styles;

export type PmsMattersOverviewProps = {
  pmsName: string;
  onViewAll: () => void;
};

export const PMS_MATTERS_OVERVIEW_QUERY = graphql(/* GraphQL */ `
  query FilteredExternalMattersCountWithAmount {
    filteredExternalMattersCountWithAmount {
      totalAmount
      totalCount
    }
  }
`);

export const PmsMattersOverview: FC<PmsMattersOverviewProps> = ({ pmsName, onViewAll }) => {
  const [shouldShake, setShouldShake] = useState(false);
  const hasShaken = useRef(false);

  const { settings } = useSettings();

  const currencySymbol = settings?.currency.symbol || "£";

  const { data: overviewData, loading: loadingOverviewData } = useQuery(
    PMS_MATTERS_OVERVIEW_QUERY,
    {
      onCompleted: (data) => {
        const amount = Math.round(data?.filteredExternalMattersCountWithAmount?.totalAmount ?? 0);

        if (!hasShaken.current && amount > 0) {
          setShouldShake(true);
          setTimeout(() => {
            setShouldShake(false);
          }, 3000);
          hasShaken.current = true;
        }
      },
    }
  );

  const totalAmount = Math.round(
    overviewData?.filteredExternalMattersCountWithAmount?.totalAmount ?? 0
  );
  const totalCount = overviewData?.filteredExternalMattersCountWithAmount?.totalCount ?? 0;

  if (loadingOverviewData) {
    return (
      <div className={overviewCardClassName}>
        <Card loading={true} />
      </div>
    );
  }

  if (totalAmount === 0) {
    return (
      <div className={emptyStateClassName}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: "var(--text-color-secondary)", fontSize: "11px" }}>
              All {pmsName} matters linked
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div
      className={`${overviewCardClassName} ${shouldShake ? shakeClassName : ""}`}
      onClick={onViewAll}
      style={{ cursor: "pointer" }}
    >
      <div className={statsSectionClassName}>
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "10px",
            background: "var(--primary-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <WarningOutlined style={{ fontSize: "28px", color: "var(--white)" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <span
              style={{
                color: "var(--heading-color)",
                fontSize: "24px",
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {formatPrice(totalAmount, currencySymbol)}
            </span>
            <span
              style={{
                color: "var(--heading-color)",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              untracked in VPD
            </span>
          </div>
          <p
            style={{
              color: "var(--text-color-secondary)",
              margin: 0,
              fontSize: "13px",
              lineHeight: 1.4,
              marginBottom: "4px",
            }}
          >
            {totalCount} {totalCount === 1 ? "matter" : "matters"} are unlinked or not tracked,
            click here to view
          </p>
        </div>
      </div>
      <div className={actionSectionClassName}>
        <EyeOutlined style={{ fontSize: "24px", color: "var(--primary-color)" }} />
      </div>
    </div>
  );
};
